import React, { FC, useEffect, useRef, useState } from "react";
import classes from "./MediaDetails.module.css";
import MediumMovieCard from "../Drag-Cards/MediumMovieCard";
import { convertMinutesToHoursAndMinutes } from "../../Utils/helpers";
import { motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../../Store/hooks";
import { toast } from "react-toastify";
import { setAuthModal } from "../../Slices/authModalSlice";
import { InputText } from "primereact/inputtext";
import {
  useAddItemInWatchlistMutation,
  useCreateWatchlistMutation,
  useGetUserWatchlistQuery,
  useRemoveItemFromWatchlistMutation,
} from "../../Slices/httpSlice";
import moment from "moment-timezone";
import { toggleWatchlistModal } from "../../Slices/watchlistModalSlice";
import { toggleWatchProvidersModal } from "../../Slices/watchProvidersModalSlice";

type MediaDetailsProps = {
  mediaType: "movie" | "tv";
  mediaDetails: any;
  windowWidth: number;
  watchProviders?: any;
  watchProvidersLength?: any;
  wpLoading?: boolean;
};

const MediaDetails: FC<MediaDetailsProps> = ({
  mediaType,
  mediaDetails,
  windowWidth,
  watchProviders,
  watchProvidersLength,
  wpLoading,
}) => {
  const { user } = useAppSelector((state) => state?.userSlice);
  const [open, setOpen] = useState<boolean>(false);
  const [listCreate, setListCreate] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const targetRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  const [createWatchlistApi, { isLoading: createWatchlistLoading }] =
    useCreateWatchlistMutation();

  const [addItemInWatchlistApi, { isLoading: addItemInWatchlistLoading }] =
    useAddItemInWatchlistMutation();

  const [
    removeItemFromWatchlistApi,
    { isLoading: removeItemFromWatchlistLoading },
  ] = useRemoveItemFromWatchlistMutation();

  const [watchlist, setWatchlist] = useState("");

  const {
    data: watchlists,
    refetch: watchlistsRefetch,
    isFetching: watchlistsLoading,
    isError: watchlistsError,
  } = useGetUserWatchlistQuery(
    {
      user_id: user?.id,
      token: user?.token,
    },
    {
      skip: !open,
    }
  );

  useEffect(() => {
    if (open) {
      watchlistsRefetch();
    }
  }, [open]);

  useEffect(() => {
    if (targetRef.current && open) {
      setHeight(targetRef.current.offsetHeight);
    } else {
      setHeight(0);
    }
  }, [
    targetRef,
    open,
    listCreate,
    watchlists,
    watchlistsLoading,
    removeItemFromWatchlistLoading,
    addItemInWatchlistLoading,
  ]);

  const handleWishlist = () => {
    if (!user) {
      toast.info("သည်းခံပါ သင်၏ကိုယ်ပိုင်စောင့်ကြည့်စာရင်းပြုလုပ်ရန် ကျေးဇူးပြု၍ လက်မှတ်ထိုးပါ သို့မဟုတ် စာရင်းသွင်းပါ။");
      setTimeout(() => {
        dispatch(setAuthModal({ authModal: true, authModalType: "signin" }));
      }, 300);
    } else {
      setHeight(0);
      setOpen((prevValue) => !prevValue);
    }
  };

  const handleCreateList = () => {
    setWatchlist("");
    setListCreate((prevValue) => !prevValue);
  };

  const handleAddList = async () => {
    if (watchlist.trim().length === 0) {
      toast.error("ကျေးဇူးပြု၍ စာရင်း၏အမည်ကိုထည့်ပါ။");
      return;
    }
    const response: any = await createWatchlistApi({
      body: {
        user_id: user.id,
        watchlist: watchlist,
      },
      token: user?.token,
    });

    if (response?.error) {
      if (response?.error?.status === 403) {
        toast.error("ကျေးဇူးပြု၍ ထပ်မံဝင်ရောက်ပါ၊ တိုကင်သက်တမ်းကုန်ပါပြီ။");
      } else if (response.error?.data?.error?.status === 0) {
        toast.error("ဤအမည်ဖြင့် စောင့်ကြည့်စာရင်း ရှိနှင့်ပြီးဖြစ်သည်။");
      } else {
        toast.error(
          response?.error?.data?.error?.error ||
          response?.error?.data?.error ||
          response?.error?.data?.message ||
          response?.error?.message ||
          "Something Went Wrong!" 
        );
      }
    } else {
      setListCreate(false);
      setWatchlist("");
      watchlistsRefetch();
      toast.success("စောင့်ကြည့်စာရင်းကို အောင်မြင်စွာ ဖန်တီးပြီးပါပြီ။");
    }
  };

  const handleAddToSpecificWatchlist = async (data: any) => {
    if (
      data?.watchlist_items.filter(
        (dataItem: any) =>
          dataItem?.media_id === mediaDetails?.result.id &&
          dataItem?.media_type === mediaType
      ).length > 0
    ) {
      const response: any = await removeItemFromWatchlistApi({
        body: {
          watchlist_id: data?.watchlist_id,
          media_id: mediaDetails?.result?.id,
          media_type: mediaType,
        },
        token: user?.token,
      });

      if (response?.error) {
        if (response?.error?.status === 403) {
          toast.error("ကျေးဇူးပြု၍ ထပ်မံဝင်ရောက်ပါ၊ တိုကင်သက်တမ်းကုန်ပါပြီ။");
        } else {
          toast.error(
            response?.error?.data?.error?.error ||
            response?.error?.data?.error ||
            response?.error?.data?.message ||
            response?.error?.message ||
            "Something Went Wrong!" 
          );
        }
      } else {
        watchlistsRefetch();
        toast.success("စောင့်ကြည့်စာရင်းမှ အရာကို အောင်မြင်စွာ ဖယ်ရှားလိုက်ပါပြီ။");
      }
    } else {
      const response: any = await addItemInWatchlistApi({
        body: {
          watchlist_id: data?.watchlist_id,
          media_id: mediaDetails?.result?.id,
          // media_type: mediaType === "movies" ? "movie" : "tv",
          media_type: mediaType,
          media_name: mediaDetails?.result?.title ?? mediaDetails?.result?.name,
          media_backdrop: mediaDetails?.result?.backdrop_path,
          media_poster: mediaDetails?.result?.poster_path,
          media_release_year:
            mediaType === "movie"
              ? new Date(mediaDetails?.result?.release_date).getFullYear()
              : new Date(mediaDetails?.result?.first_air_date).getFullYear(),
        },
        token: user?.token,
      });

      if (response?.error) {
        if (response?.error?.status === 403) {
          toast.error("ကျေးဇူးပြု၍ ထပ်မံဝင်ရောက်ပါ၊ တိုကင်သက်တမ်းကုန်ပါပြီ။");
        } else if (response.error?.data?.error?.status === 0) {
          toast.error("ဤကြည့်ရှုမှုစာရင်းတွင် အကြောင်းအရာကို ထည့်သွင်းပြီးဖြစ်သည်။");
        } else {
          toast.error(
            response?.error?.data?.error?.error ||
            response?.error?.data?.error ||
            response?.error?.data?.message ||
            response?.error?.message ||
            "Something Went Wrong!" 
          );
        }
      } else {
        watchlistsRefetch();
        toast.success("စောင့်ကြည့်စာရင်းတွင် အကြောင်းအရာကို အောင်မြင်စွာ ထည့်သွင်းခဲ့သည်။");
      }
    }
  };

  console.log(watchProviders?.[Object.keys(watchProviders ?? {})[1]], "wpo");

  return (
    <div className={classes.movie_details_grid}>
      <MediumMovieCard data={mediaDetails?.result} navigateTo={mediaType} />
      <div className={classes.movie_details_brief}>
        {mediaType === "tv" ? (
          <p className={classes.title}>
            {mediaDetails?.result?.title ?? mediaDetails?.result?.name}{" "}
            <span className={classes.span}>
              ({new Date(mediaDetails?.result?.first_air_date).getFullYear()}
              {mediaDetails?.result?.status === "Ended"
                ? ` - ${new Date(
                    mediaDetails?.result?.last_air_date
                  ).getFullYear()}`
                : ` - `}
              )
            </span>
          </p>
        ) : (
          <p className={classes.title}>
            {mediaDetails?.result?.title ?? mediaDetails?.result?.name}{" "}
            <span className={classes.span}>
              ({new Date(mediaDetails?.result?.release_date).getFullYear()})
            </span>
          </p>
        )}

        <div className={classes.info_container}>
          {mediaDetails?.result?.status === "Released" ? (
            <>
              <div className={classes.duration}>
                <i className={`pi pi-circle-fill ${classes.bullet}`} />
                <p className={classes.clock_time}>
                  {convertMinutesToHoursAndMinutes(
                    mediaDetails?.result?.runtime
                  )}
                </p>
              </div>
              <div className={classes.rating}>
                <img
                  src="/assets/images/star.png"
                  className={classes.star}
                  alt="star"
                />
                <p className={classes.rating_number}>
                  {mediaDetails?.result?.vote_average}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className={classes.duration}>
                <i className={`pi pi-circle-fill ${classes.bullet}`} />
                <p className={classes.clock_time}>
                  {mediaDetails?.result?.status}
                </p>
              </div>
              <div className={classes.rating}>
                <img
                  src="/assets/images/star.png"
                  className={classes.star}
                  alt="star"
                />
                {/* <i className={`pi pi-circle-fill ${classes.bullet}`} /> */}
                <p className={classes.rating_number}>
                  {mediaDetails?.result?.vote_average}
                </p>
              </div>
            </>
          )}
        </div>
        <div className={classes.overview_section}>
          <motion.div
            className={classes.genres}
            style={{
              gridTemplateColumns: `repeat(${mediaDetails?.result?.genres?.length},100px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -(windowWidth <= 800
                ? 10 +
                  (mediaDetails?.result?.genres?.length - 1) * 100 +
                  (mediaDetails?.result?.genres?.length - 1) * 10
                : window.innerWidth * 0.05 +
                  (mediaDetails?.result?.genres?.length - 1) * 100 +
                  (mediaDetails?.result?.genres?.length - 1) * 10),
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {mediaDetails?.result?.genres?.map(
              (genre: { name: string; id: number }) => (
                <p className={classes.genre}>{genre?.name}</p>
              )
            )}
          </motion.div>
          <p className={classes.overview}>{mediaDetails?.result?.overview}</p>
        </div>

        <div className={classes.grid_wrapper}>
          <div className={classes.wishlist_btn} onClick={handleWishlist}>
            စောင့်ကြည့်စာရင်းသို့ ထည့်ပါ။
            <div className={classes.separator}>
              <div className={classes.line}></div>
              {open ? (
                <i className={`pi pi-chevron-up ${classes.chevron}`} />
              ) : (
                <i className={`pi pi-chevron-down ${classes.chevron}`} />
              )}
            </div>
            {open && (
              <div
                ref={targetRef}
                className={classes.wishlist_menu_items}
                style={{
                  bottom: `calc(-${height}px - 10px)`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className={classes.wrapper}
                  onClick={() => {
                    setOpen((prevValue) => !prevValue);
                    dispatch(toggleWatchlistModal());
                  }}
                >
                  <p className={classes.item}>စောင့်ကြည့်စာရင်းကို ကြည့်ပါ။</p>
                  <i
                    className={`pi pi-chevron-right
                  } ${classes.chevron}`}
                  />
                </div>
                <div className={classes.wrapper} onClick={handleCreateList}>
                  <p className={classes.item}>စာရင်းအသစ်ဖန်တီးပါ။</p>
                  <i
                    className={`pi ${
                      listCreate ? "pi-chevron-up" : "pi-chevron-right"
                    } ${classes.chevron}`}
                  />
                </div>

                {listCreate ? (
                  <div className={classes.inputs}>
                    <InputText
                      type="text"
                      className={`p-inputtext-lg ${classes.input}`}
                      placeholder="List Title"
                      autoFocus={true}
                      value={watchlist}
                      onChange={(e) => setWatchlist(e.target.value)}
                    />
                    <button
                      type="button"
                      className={classes.add_btn}
                      onClick={handleAddList}
                      disabled={createWatchlistLoading}
                    >
                      {createWatchlistLoading ? (
                        <div className={classes.spinner}></div>
                      ) : (
                        "ထည့်ပါ။"
                      )}
                    </button>
                  </div>
                ) : watchlistsLoading ||
                  addItemInWatchlistLoading ||
                  removeItemFromWatchlistLoading ? (
                  <div className={classes.message}>
                    <div className={classes.spinner}></div>
                  </div>
                ) : watchlists?.result.length === 0 ? (
                  <p className={classes.message}>စာရင်းမတွေ့ပါ။</p>
                ) : (
                  <div className={classes.watchlist_wrapper}>
                    {watchlists.result.map((data: any, idx: number) => {
                      return (
                        <div
                          key={idx}
                          className={classes.watchlist}
                          onClick={() => handleAddToSpecificWatchlist(data)}
                        >
                          <div className={classes.watchlist_txt}>
                            {data?.watchlist_items.filter(
                              (dataItem: any) =>
                                dataItem?.media_id ===
                                  mediaDetails?.result.id &&
                                dataItem?.media_type === mediaType
                            ).length > 0 ? (
                              <i className={`pi pi-check ${classes.plus}`} />
                            ) : (
                              <i className={`pi pi-plus ${classes.plus}`} />
                            )}
                            <p>{data?.watchlist_name}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {!wpLoading &&
            watchProviders &&
            Object.keys(watchProviders ?? {})[1] && (
              <button
                type="button"
                className={classes.watch_providers}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(toggleWatchProvidersModal());
                }}
              >
                {watchProviders?.[Object.keys(watchProviders ?? {})[1]].map(
                  (dataItem: any, idx: number) => {
                    if (idx <= 3) {
                      return (
                        <div
                          data-index={idx + 1}
                          className={`${classes.watch_provider} ${
                            idx > 0 && classes.shift_left
                          }`}
                          key={idx}
                        >
                          <img
                            src={`https://media.themoviedb.org/t/p/original/${dataItem?.logo_path}`}
                            alt={dataItem?.provider_name}
                            className={classes.provider_img}
                          />
                        </div>
                      );
                    }
                  }
                )}
                {/* {watchProvidersLength -
                  watchProviders?.[Object.keys(watchProviders ?? {})[1]]
                    .length >
                  0 && ( */}
                <div className={classes.watch_provider_3}>
                  <p className={classes.watch_provider_length}>
                    +
                    {watchProvidersLength -
                      watchProviders?.[Object.keys(watchProviders ?? {})[1]]
                        .length >
                    3
                      ? 3
                      : watchProviders?.[Object.keys(watchProviders ?? {})[1]]
                          .length}
                  </p>
                </div>
                {/* )} */}
              </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default MediaDetails;
